<template>
    <UiModal
        v-if="!isMobile"
        :isOpen="show && !showManageEmails"
        @close="emit('close')"
        width="600px"
        :fullScreenMobile="true"
    >
        <template #title>
            <div></div>
        </template>

        <template #content>
            <!-- Paid user modal -->
            <div
                v-if="isSubscribed"
                class="flex flex-col justify-center items-center"
            >
                <img
                    src="/images/learn-more-redemption-paid.png"
                    alt="Import redemptions"
                    class="w-[200px] h-auto mb-6"
                />

                <h3
                    class="text-[20px] font-medium leading-7 text-slate-800 dark:text-slate-100 text-center mb-6"
                >
                    Import redemptions from email
                </h3>

                <div
                    class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 self-stretch mb-2 w-full text-left"
                >
                    Forward your redemption confirmation emails to:
                </div>

                <div
                    class="flex items-center justify-between w-full bg-slate-50 dark:bg-slate-900/40 rounded-lg px-4 py-3 mb-6"
                >
                    <span
                        class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 flex-1"
                    >
                        redemptions@10xtravel.com
                    </span>
                    <Tooltip yPlacement="top" xPlacement="center">
                        <template #trigger>
                            <div
                                @click="copyEmail"
                                class="text-slate-400 dark:text-slate-300 hover:text-slate-600 dark:hover:text-slate-100 p-1"
                            >
                                <Icon icon="copy" :width="20" :height="20" />
                            </div>
                        </template>
                        <span class="flex items-center gap-1 whitespace-nowrap">
                            <Icon
                                v-if="isCopied"
                                icon="check"
                                :width="14"
                                :height="14"
                                class="text-white"
                            />
                            {{ copyTooltipText }}
                        </span>
                    </Tooltip>
                </div>

                <div class="flex flex-col gap-4 w-full mb-6">
                    <div class="flex items-center gap-3">
                        <span
                            class="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0"
                        >
                            1
                        </span>
                        <span
                            class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 flex-1"
                        >
                            Open any booking confirmation email
                        </span>
                    </div>
                    <div class="flex items-center gap-3">
                        <span
                            class="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0"
                        >
                            2
                        </span>
                        <span
                            class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 flex-1"
                        >
                            Forward to the above email address from your
                            {{
                                allSenderEmails.length > 1 ? 'emails' : 'email'
                            }}: {{ allSenderEmails.join(', ') }}
                        </span>
                    </div>
                    <div class="flex items-center gap-3">
                        <span
                            class="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0"
                        >
                            3
                        </span>
                        <span
                            class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 flex-1"
                        >
                            Redemption will automatically be created and added
                            to your trip
                        </span>
                    </div>
                </div>
            </div>

            <!-- Free user modal -->
            <div
                v-else
                class="flex flex-col justify-center items-center pt-6 w-full px-16"
            >
                <img
                    src="/images/learn-more-redemption-paid.png"
                    alt="Import redemptions"
                    class="w-[200px] h-auto mb-6"
                />

                <h3
                    class="text-[20px] font-medium leading-7 text-center mb-1 w-full"
                >
                    <span class="text-[#FD421D]">Upgrade to 10xTravel+</span>
                </h3>
                <h3
                    class="text-[20px] font-medium leading-7 text-slate-800 dark:text-slate-100 text-center mb-3 w-full"
                >
                    to import redemptions from email
                </h3>

                <p
                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 text-center self-stretch mb-6 w-full"
                >
                    Forward hotel and flight confirmation emails and we'll
                    automatically create the redemption for you.
                </p>

                <div class="flex flex-col gap-4 w-full mb-6">
                    <div class="flex items-center gap-3">
                        <svg
                            class="shrink-0"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M16.667 5L7.5 14.167L3.333 10"
                                stroke="#FD421D"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                        <span
                            class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                        >
                            Automatically adds redemptions to your trips
                        </span>
                    </div>
                    <div class="flex items-center gap-3">
                        <svg
                            class="shrink-0"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M16.667 5L7.5 14.167L3.333 10"
                                stroke="#FD421D"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                        <span
                            class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                        >
                            No manual data entry
                        </span>
                    </div>
                    <div class="flex items-center gap-3">
                        <svg
                            class="shrink-0"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M16.667 5L7.5 14.167L3.333 10"
                                stroke="#FD421D"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                        <span
                            class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                        >
                            Only works with email addresses you approve
                        </span>
                    </div>
                </div>

                <a
                    href="/10xtravel-plus/upgrade"
                    @click.prevent="navigateToUpgrade"
                    class="flex items-center justify-center w-full px-4 py-2.5 rounded bg-10x-orange shadow-sm text-sm font-medium leading-5 text-white hover:bg-[#e53b19] mb-3 mt-6"
                >
                    Upgrade to 10xTravel+
                </a>
                <a
                    href="/10xtravel-plus"
                    @click.prevent="navigateToLearnMore"
                    class="flex px-4 py-2.5 justify-center items-center gap-2 self-stretch rounded text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                    Learn more
                </a>
            </div>
        </template>

        <template #footer>
            <!-- Paid user footer -->
            <div
                v-if="isSubscribed"
                class="flex flex-col sm:flex-row items-center justify-between gap-3 w-full"
            >
                <a
                    href="/settings/email-import"
                    @click.prevent="openManageEmails"
                    class="flex items-center gap-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:hover:text-slate-300 cursor-pointer rounded"
                >
                    <Icon icon="settings" :width="20" :height="20" />
                    Manage sender emails
                </a>
                <button
                    @click="emit('close')"
                    class="flex items-center justify-center px-4 py-2 rounded-full bg-[#FD421D] shadow-sm text-sm font-medium leading-5 text-white hover:bg-[#e53b19]"
                >
                    Got it
                </button>
            </div>

            <!-- Free user footer (empty since buttons are in content now) -->
            <div v-else></div>
        </template>
    </UiModal>

    <UiModalMobile
        v-else
        :isOpen="show && !showManageEmails"
        @close="emit('close')"
        title=""
    >
        <template #header>
            <div class="flex items-center justify-end w-full">
                <button
                    @click="emit('close')"
                    class="btn-base btn-icon !m-0"
                    type="button"
                >
                    <Icon icon="x" class="text-slate-500 dark:text-slate-400" />
                </button>
            </div>
        </template>

        <template #content>
            <!-- Paid user modal -->
            <div
                v-if="isSubscribed"
                class="flex flex-col justify-center items-center pt-6"
            >
                <img
                    src="/images/learn-more-redemption-paid.png"
                    alt="Import redemptions"
                    class="w-[200px] h-auto mb-6"
                />

                <h3
                    class="text-[20px] font-medium leading-7 text-slate-800 dark:text-slate-100 text-center mb-3"
                >
                    Import redemptions from email
                </h3>

                <p
                    class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 text-center self-stretch mb-4"
                >
                    Forward your flight and hotel confirmation emails to this
                    address and they'll appear in your trips automatically:
                </p>

                <div
                    class="flex items-center justify-between w-full bg-slate-50 dark:bg-slate-900/40 rounded-lg px-4 py-3 mb-6"
                >
                    <span
                        class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 flex-1"
                    >
                        redemptions@10xtravel.com
                    </span>
                    <Tooltip yPlacement="top" xPlacement="center">
                        <template #trigger>
                            <div
                                @click="copyEmail"
                                class="text-slate-400 dark:text-slate-300 hover:text-slate-600 dark:hover:text-slate-100 p-1"
                            >
                                <Icon icon="copy" :width="20" :height="20" />
                            </div>
                        </template>
                        <span class="flex items-center gap-1 whitespace-nowrap">
                            <Icon
                                v-if="isCopied"
                                icon="check"
                                :width="14"
                                :height="14"
                                class="text-white"
                            />
                            {{ copyTooltipText }}
                        </span>
                    </Tooltip>
                </div>

                <div class="flex flex-col gap-4 w-full mb-6">
                    <div class="flex items-center gap-3">
                        <span
                            class="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0"
                        >
                            1
                        </span>
                        <span
                            class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 flex-1"
                        >
                            Open any booking confirmation email
                        </span>
                    </div>
                    <div class="flex items-center gap-3">
                        <span
                            class="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0"
                        >
                            2
                        </span>
                        <span
                            class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 flex-1"
                        >
                            Forward to the above email address from your
                            {{
                                allSenderEmails.length > 1 ? 'emails' : 'email'
                            }}: {{ allSenderEmails.join(', ') }}
                        </span>
                    </div>
                    <div class="flex items-center gap-3">
                        <span
                            class="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0"
                        >
                            3
                        </span>
                        <span
                            class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 flex-1"
                        >
                            Redemption will be automatically created and added
                            to your trip
                        </span>
                    </div>
                </div>
            </div>

            <!-- Free user modal -->
            <div
                v-else
                class="flex flex-col justify-center items-center pt-6 w-full px-6"
            >
                <img
                    src="/images/learn-more-redemption-paid.png"
                    alt="Import redemptions"
                    class="w-[200px] h-auto mb-6"
                />

                <h3
                    class="text-[20px] font-medium leading-7 text-center mb-1 w-full"
                >
                    <span class="text-[#FD421D]">Upgrade to 10xTravel+</span>
                </h3>
                <h3
                    class="text-[20px] font-medium leading-7 text-slate-800 dark:text-slate-100 text-center mb-3 w-full"
                >
                    to import redemptions from email
                </h3>

                <p
                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 text-center self-stretch mb-6 w-full"
                >
                    Forward hotel and flight confirmation emails and we'll
                    automatically create the redemption for you.
                </p>

                <div class="flex flex-col gap-4 w-full mb-6">
                    <div class="flex items-center gap-3">
                        <svg
                            class="shrink-0"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M16.667 5L7.5 14.167L3.333 10"
                                stroke="#FD421D"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                        <span
                            class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                        >
                            Automatically adds redemptions to your trips
                        </span>
                    </div>
                    <div class="flex items-center gap-3">
                        <svg
                            class="shrink-0"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M16.667 5L7.5 14.167L3.333 10"
                                stroke="#FD421D"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                        <span
                            class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                        >
                            No manual data entry
                        </span>
                    </div>
                    <div class="flex items-center gap-3">
                        <svg
                            class="shrink-0"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M16.667 5L7.5 14.167L3.333 10"
                                stroke="#FD421D"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                        <span
                            class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                        >
                            Only works with email addresses you approve
                        </span>
                    </div>
                </div>

                <a
                    href="/10xtravel-plus/upgrade"
                    @click.prevent="navigateToUpgrade"
                    class="flex items-center justify-center w-full px-4 py-2.5 rounded bg-[#FD421D] shadow-sm text-sm font-medium leading-5 text-white hover:bg-[#e53b19] mb-3"
                >
                    Upgrade to 10xTravel+
                </a>
                <a
                    href="/10xtravel-plus"
                    @click.prevent="navigateToLearnMore"
                    class="flex px-4 py-2.5 justify-center items-center gap-2 self-stretch rounded text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                    Learn more
                </a>
            </div>
        </template>

        <template #footer>
            <!-- Paid user footer -->
            <div
                v-if="isSubscribed"
                class="flex flex-col sm:flex-row items-center justify-between gap-3 w-full"
            >
                <a
                    href="/settings/email-import"
                    @click.prevent="openManageEmails"
                    class="flex items-center gap-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:hover:text-slate-300 cursor-pointer rounded"
                >
                    <Icon icon="settings" :width="20" :height="20" />
                    Manage sender emails
                </a>
                <button
                    @click="emit('close')"
                    class="flex items-center justify-center px-4 py-2 rounded-full bg-[#FD421D] shadow-sm text-sm font-medium leading-5 text-white hover:bg-[#e53b19]"
                >
                    Got it
                </button>
            </div>

            <!-- Free user footer (empty since buttons are in content now) -->
            <div v-else></div>
        </template>
    </UiModalMobile>

    <!-- Manage sender emails modal -->
    <ManageSenderEmailsModal
        :show="showManageEmails"
        :userEmail="userEmail"
        @close="showManageEmails = false"
    />
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { router } from '@inertiajs/vue3';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import Icon from '@/components/ui/icon/icon.vue';
import Tooltip from '@/components/tooltip.vue';
import ManageSenderEmailsModal from '@/components/trips/modals/manage-sender-emails-modal.vue';
import { Apii } from '@/services/api';
import { useIsMobile } from '@/composables/useIsMobile';

const { isMobile } = useIsMobile(640);

const ApiService = new Apii();

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    isSubscribed: {
        type: Boolean,
        default: false,
    },
    userEmail: {
        type: String,
        default: 'your email',
    },
});

const emit = defineEmits(['close']);

const showManageEmails = ref(false);
const forwardingEmails = ref([]);
const copyTooltipText = ref('Copy link');
const isCopied = ref(false);
let copyTooltipTimer = null;

const allSenderEmails = computed(() => {
    const emails = [props.userEmail];
    forwardingEmails.value.forEach((e) => {
        if (e.email && e.email !== props.userEmail) emails.push(e.email);
    });
    return emails;
});

const fetchForwardingEmails = async () => {
    try {
        const response = await ApiService.apiCall(
            '/user/forwarding-emails',
            'get'
        );
        forwardingEmails.value = (response.data?.forwarding_emails || []).filter(
            (e) => e.verified
        );
    } catch {
        forwardingEmails.value = [];
    }
};

watch(
    () => props.show,
    (val) => {
        if (val && props.isSubscribed) fetchForwardingEmails();
    },
    { immediate: true }
);

const copyEmail = async () => {
    try {
        await navigator.clipboard.writeText('redemptions@10xtravel.com');
        copyTooltipText.value = 'Copied';
        isCopied.value = true;
        if (copyTooltipTimer) clearTimeout(copyTooltipTimer);
        copyTooltipTimer = setTimeout(() => {
            copyTooltipText.value = 'Copy link';
            isCopied.value = false;
        }, 2000);
    } catch {
        // Fallback silently
    }
};

const openManageEmails = () => {
    showManageEmails.value = true;
};

const navigateToUpgrade = () => {
    emit('close');
    window.open('/10xtravel-plus/upgrade', '_blank');
};

const navigateToLearnMore = () => {
    emit('close');
    window.open('/10xtravel-plus', '_blank');
};
</script>
